import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import { useOnSiteStructureChanged } from "../../../hooks/useOnSiteStructureChanged";
import urls from "../../../urls";
import { COMPONENT_QUERY_KEY } from "../../../utils/constants";
import { ObjectName } from "../../../utils/enums";
import type {
  ConnectionCreateRequest,
  ConnectionResponse,
  ConnectionUpdateRequest
} from "../Connection.types";
import {
  connectionQueries,
  mergeConnectionIntoListCache
} from "./useConnection";

export function useConnectionMutation(siteId: number) {
  const queryClient = useQueryClient();
  const cacheName = COMPONENT_QUERY_KEY[ObjectName.Connection];
  const onSiteStructureChanged = useOnSiteStructureChanged(siteId);

  const createMutation = useMutation({
    mutationFn: (createRequest: ConnectionCreateRequest) =>
      createConnection(createRequest, siteId),
    onSuccess: (response) => {
      mergeConnectionIntoListCache(
        queryClient,
        { siteOrVariantId: siteId },
        response.data
      );

      if (response.data.ghost_node) {
        queryClient.invalidateQueries({
          queryKey: [
            COMPONENT_QUERY_KEY[ObjectName.GhostNode],
            { siteOrVariantId: siteId }
          ]
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
    }
  });

  const editMutation = useMutation({
    mutationFn: (updatedData: ConnectionUpdateRequest) =>
      editConnection(updatedData),
    onSuccess(response) {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      queryClient.invalidateQueries({
        queryKey: connectionQueries.detail(response.data.id).queryKey
      });
      if (response.data.ghost_node) {
        queryClient.invalidateQueries({
          queryKey: [
            COMPONENT_QUERY_KEY[ObjectName.GhostNode],
            { siteOrVariantId: siteId }
          ]
        });
      }
      onSiteStructureChanged();
    }
  });

  return {
    createMutation,
    editMutation
  };
}

function createConnection(data: ConnectionCreateRequest, siteId: number) {
  const url = urls.apiWithoutCamelization.connections(siteId);
  return apiWithoutCamelization.post<ConnectionResponse>(url, data);
}

function editConnection(data: ConnectionUpdateRequest) {
  const url = urls.apiWithoutCamelization.connection(data.id);
  return apiWithoutCamelization.patch<ConnectionResponse>(url, data);
}
